import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, CircleDollarSign, FolderOpen, Landmark, MapPinned, Pencil, Save, ShieldCheck, TrendingUp, WalletCards, X } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { ArchivedClientsSection, ClientArchiveConfirm } from "@/components/ClientArchiveControls";
import { ClientDocumentLibrary } from "@/components/clients/ClientDocumentLibrary";
import { ClientSiteProfileEditor } from "@/components/clients/ClientSiteProfileEditor";
import { EditorModal } from "@/components/EditorModal";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MANAGE_ROLES = ["system_administrator", "operations"];

function currency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export default function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [editingClient, setEditingClient] = useState(null);
  const [siteClient, setSiteClient] = useState(null);
  const [docsClient, setDocsClient] = useState(null);
  const [archivingClient, setArchivingClient] = useState(null);
  const [budget, setBudget] = useState({ fiscal_year: 2026, month: "", amount: "" });

  const loadClients = useCallback(async () => {
    try {
      const response = await api.get("/clients/budgets", { params: canManage ? { include_archived: true } : {} });
      setClients(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const activeClients = clients.filter((client) => client.status !== "Archived");
  const archivedClients = clients.filter((client) => client.status === "Archived");

  async function saveBudget(event) {
    event.preventDefault();
    try {
      await api.put("/data-administration/budgets", {
        client_id: editingClient.id,
        fiscal_year: Number(budget.fiscal_year),
        month: budget.month ? Number(budget.month) : null,
        amount: Number(budget.amount),
      });
      setEditingClient(null);
      await loadClients();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  const totals = activeClients.reduce(
    (sum, client) => ({
      budget: sum.budget + client.budget_amount,
      committed: sum.committed + client.committed_spend,
      remaining: sum.remaining + client.remaining_budget,
    }),
    { budget: 0, committed: 0, remaining: 0 },
  );

  return (
    <div className="app-shell" data-testid="clients-shell">
      <AppSidebar activeItem="Clients" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page clients-page" data-testid="clients-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">CLIENT FINANCIAL HEALTH</p>
              <h1>Clients & Spend</h1>
              <p className="page-subtitle">
                Track approved budget against committed staffing spend for every client account.
              </p>
            </div>
          </div>
          {error && (
            <p className="form-message error-message" data-testid="client-budget-error">
              {error}
            </p>
          )}
          {notice && (
            <p className="form-message success-message" data-testid="clients-notice">
              {notice}
            </p>
          )}
          <section className="client-spend-summary" data-testid="client-spend-summary">
            <article>
              <Landmark size={19} />
              <span>
                <small>Approved budget</small>
                <strong>{currency(totals.budget)}</strong>
              </span>
            </article>
            <article>
              <CircleDollarSign size={19} />
              <span>
                <small>Committed spend</small>
                <strong>{currency(totals.committed)}</strong>
              </span>
            </article>
            <article>
              <WalletCards size={19} />
              <span>
                <small>Remaining budget</small>
                <strong>{currency(totals.remaining)}</strong>
              </span>
            </article>
          </section>
          <section className="client-budget-grid" data-testid="client-budget-grid">
            {activeClients.map((client) => (
              <article
                className={`client-budget-card ${client.active === false ? "is-inactive" : ""}`}
                key={client.id}
                data-testid={`client-budget-${client.id}`}
              >
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">{client.region}</p>
                    <h2>
                      {client.name}
                      {client.active === false && (
                        <span className="status-badge status-inactive client-status-badge" data-testid={`client-status-${client.id}`}>
                          Inactive
                        </span>
                      )}
                    </h2>
                  </div>
                  <div className="client-card-actions">
                    {canManage && (
                      <>
                        <button aria-label={`Site profile for ${client.name}`} className="projection-icon-button" data-testid={`client-site-profile-${client.id}`} title="Site profile" type="button" onClick={() => setSiteClient(client)}>
                          <MapPinned size={16} />
                        </button>
                        <button aria-label={`Document library for ${client.name}`} className="projection-icon-button" data-testid={`client-documents-${client.id}`} title="Document library" type="button" onClick={() => setDocsClient(client)}>
                          <FolderOpen size={16} />
                        </button>
                      </>
                    )}
                    <button
                      aria-label={`Compliance requirements for ${client.name}`}
                      className="projection-icon-button"
                      data-testid={`client-compliance-${client.id}`}
                      title="Compliance requirements"
                      type="button"
                      onClick={() => navigate(`/compliance?client=${client.id}`)}
                    >
                      <ShieldCheck size={16} />
                    </button>
                    {user?.role === "system_administrator" && (
                      <button
                        aria-label={`Edit ${client.name} budget`}
                        className="projection-icon-button"
                        data-testid={`edit-client-budget-${client.id}`}
                        type="button"
                        onClick={() => {
                          setEditingClient(client);
                          setBudget({
                            fiscal_year: 2026,
                            month: "",
                            amount: client.budget_amount || "",
                          });
                        }}
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {canManage && (
                      <button
                        aria-label={`Archive ${client.name}`}
                        className="projection-icon-button"
                        data-testid={`archive-client-${client.id}`}
                        title="Archive client"
                        type="button"
                        onClick={() => setArchivingClient(client)}
                      >
                        <Archive size={16} />
                      </button>
                    )}
                    {!canManage && <TrendingUp size={19} />}
                  </div>
                </div>
                <div className="client-budget-figures">
                  <div>
                    <small>Budget</small>
                    <strong>{currency(client.budget_amount)}</strong>
                  </div>
                  <div>
                    <small>Committed</small>
                    <strong>{currency(client.committed_spend)}</strong>
                  </div>
                  <div>
                    <small>Remaining</small>
                    <strong>{currency(client.remaining_budget)}</strong>
                  </div>
                </div>
                <div className="client-budget-progress">
                  <span style={{ width: `${Math.min(client.utilization, 100)}%` }} />
                </div>
                <div className="client-budget-footer">
                  <strong>{client.utilization}% utilized</strong>
                  <span>Committed staffing spend</span>
                </div>
              </article>
            ))}
            {loading && <div className="empty-requests list-loading" data-testid="clients-loading">Loading…</div>}
            {!loading && !error && !activeClients.length && (
              <div className="empty-requests" data-testid="clients-empty">No client budget records are available.</div>
            )}
          </section>
          {canManage && (
            <ArchivedClientsSection
              clients={archivedClients}
              onRestore={async (client) => {
                setNotice(`${client.name} restored to the active client list.`);
                await loadClients();
              }}
            />
          )}
          {archivingClient && (
            <ClientArchiveConfirm
              client={archivingClient}
              onClose={() => setArchivingClient(null)}
              onArchived={async (client) => {
                setArchivingClient(null);
                setNotice(`${client.name} archived. Restore it any time from Archived clients below.`);
                await loadClients();
              }}
            />
          )}
          {siteClient && (
            <EditorModal onClose={() => setSiteClient(null)} testId="client-site-profile-modal">
              <ClientSiteProfileEditor client={siteClient} onClose={() => setSiteClient(null)} onSaved={(result) => { setSiteClient(null); setNotice(`${result.client_name} site profile saved${result.changes?.length ? ` (${result.changes.join(", ")})` : ""}${result.assignments_updated ? ` · ${result.assignments_updated} provider${result.assignments_updated === 1 ? "" : "s"} sent “Assignment updated”` : ""}.`); }} />
            </EditorModal>
          )}
          {docsClient && (
            <EditorModal onClose={() => setDocsClient(null)} testId="client-documents-modal">
              <ClientDocumentLibrary client={docsClient} onClose={() => setDocsClient(null)} />
            </EditorModal>
          )}
          {editingClient && (
            <EditorModal onClose={() => setEditingClient(null)} testId="client-budget-editor-modal">
            <section className="inline-edit-panel" data-testid="client-budget-editor">
              <div className="section-heading">
                <div>
                  <h2>Edit {editingClient.name} budget</h2>
                  <p>Save an annual budget or a monthly SPED budget directly from Clients.</p>
                </div>
                <button
                  className="projection-icon-button"
                  data-testid="close-client-budget-editor"
                  type="button"
                  onClick={() => setEditingClient(null)}
                >
                  <X size={16} />
                </button>
              </div>
              <form className="inline-edit-form" onSubmit={saveBudget}>
                <label>
                  Fiscal year
                  <input
                    data-testid="client-budget-year-input"
                    min="2000"
                    type="number"
                    value={budget.fiscal_year}
                    onChange={(event) => setBudget({ ...budget, fiscal_year: event.target.value })}
                  />
                </label>
                <label>
                  Month
                  <select
                    data-testid="client-budget-month-input"
                    value={budget.month}
                    onChange={(event) => setBudget({ ...budget, month: event.target.value })}
                  >
                    <option value="">Annual budget</option>
                    {Array.from({ length: 12 }, (_, index) => (
                      <option key={index + 1} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Budget amount
                  <input
                    data-testid="client-budget-amount-input"
                    min="0"
                    required
                    step="0.01"
                    type="number"
                    value={budget.amount}
                    onChange={(event) => setBudget({ ...budget, amount: event.target.value })}
                  />
                </label>
                <button className="button button-primary" data-testid="save-client-budget-button">
                  <Save size={16} /> Save budget
                </button>
              </form>
            </section>
            </EditorModal>
          )}
        </main>
      </div>
    </div>
  );
}
